import React, { ReactNode } from 'react';
import { BaseErrorBoundary, BaseErrorBoundaryProps, BaseErrorBoundaryState } from './BaseErrorBoundary';

export interface HomepageErrorBoundaryProps extends BaseErrorBoundaryProps {
  sectionName?: string;
  fallbackComponent?: React.ComponentType<{ onRetry: () => void; error?: Error; sectionName?: string }>;
}

export class HomepageErrorBoundary extends BaseErrorBoundary<HomepageErrorBoundaryProps, BaseErrorBoundaryState> {
  constructor(props: HomepageErrorBoundaryProps) {
    super({ ...props, errorType: 'Homepage' });
    this.state = {
      hasError: false,
      errorId: this.generateErrorId(),
      timestamp: new Date(),
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<BaseErrorBoundaryState> {
    return {
      hasError: true,
      error,
      errorId: `homepage_error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
    };
  }

  protected renderErrorUI(): ReactNode {
    if (this.props.fallbackComponent) {
      const FallbackComponent = this.props.fallbackComponent;
      return (
        <FallbackComponent
          onRetry={this.handleRetry}
          error={this.state.error}
          sectionName={this.props.sectionName}
        />
      );
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <section
        className="py-12 bg-neutral-50"
        role="alert"
        aria-live="polite"
        aria-labelledby={`error-${this.props.sectionName || 'section'}-title`}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-lg mx-auto text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-yellow-100 rounded-full">
              <svg className="h-8 w-8 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 id={`error-${this.props.sectionName || 'section'}-title`} className="text-lg font-semibold text-neutral-900 mb-2">
              Unable to load {this.props.sectionName || 'section'}
            </h3>
            <p className="text-neutral-600 mb-4">
              We&apos;re having trouble loading this section. This doesn&apos;t affect other parts of the page.
            </p>
            <button
              onClick={this.handleRetry}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              aria-describedby={`retry-${this.props.sectionName || 'section'}-description`}
            >
              <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Retry Loading
            </button>
            <div id={`retry-${this.props.sectionName || 'section'}-description`} className="sr-only">
              Attempt to reload the {this.props.sectionName || 'section'} section
            </div>
          </div>
        </div>
      </section>
    );
  }
}

export const withHomepageErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  sectionName: string
) => {
  const WrappedComponent = React.forwardRef<unknown, P>((props, ref) => (
    <HomepageErrorBoundary sectionName={sectionName}>
      <Component {...props} ref={ref} />
    </HomepageErrorBoundary>
  ));

  WrappedComponent.displayName = `withHomepageErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
};
